import { createWithEqualityFn } from 'zustand/traditional';

import { FavoriteMajorSurvey, SurveyResult } from '@common-types';

import { SurveyQuestionsData } from '@interfaces/favoriteSurvey';

import { SURVEYS_ID } from '@constants/surveys';
import { postMajorFavoriteSurvey } from '@api/surveys';

import useUserPreferencesStore from '@store/UserPreferencesStore';

interface MajorSurveyData {
  [key: string]: SurveyQuestionsData;
}

interface MajorSurveyDataStore {
  favoriteMajorSurveys: MajorSurveyData;
  isLoading: boolean;
  getFavoriteMajorSurvey: (programCipCode: string) => Promise<SurveyQuestionsData>;
  setFavoriteMajorSurvey: (data: FavoriteMajorSurvey) => void;
}

const initialState = {
  favoriteMajorSurveys: {} as MajorSurveyData,
  isLoading: false,
} as MajorSurveyDataStore;

export const useMajorSurveyStore = createWithEqualityFn<MajorSurveyDataStore>()(
  (set, get) => ({
    ...initialState,

    getFavoriteMajorSurvey: async (programCipCode: string) => {
      set({ isLoading: true });

      const favoriteMajors = useUserPreferencesStore.getState().majorFavorites;

      const surveyAnswers =
        favoriteMajors.cipCodeInformation
          ?.find((major) => major.cipCode === programCipCode)
          ?.surveys?.find((survey) => survey.surveyId === SURVEYS_ID.FAVORITE_MAJORS)?.questions || [];

      const preparedData: SurveyQuestionsData = surveyAnswers.reduce(
        (previousValue: SurveyQuestionsData, currentValue: SurveyResult) => {
          // eslint-disable-next-line
          previousValue[currentValue.questionId] = currentValue.answer;

          return previousValue;
        },
        {},
      );

      const favoriteMajorSurveys = { ...get().favoriteMajorSurveys };

      favoriteMajorSurveys[programCipCode] = preparedData;

      set({ favoriteMajorSurveys, isLoading: false });

      return get().favoriteMajorSurveys[programCipCode];
    },

    setFavoriteMajorSurvey: async (data: FavoriteMajorSurvey) => {
      const userPreferences = await postMajorFavoriteSurvey(data);

      const preparedData: SurveyQuestionsData = data.questions.reduce(
        (previousValue: SurveyQuestionsData, currentValue: SurveyResult) => {
          // eslint-disable-next-line
          previousValue[currentValue.questionId] = currentValue.answer;

          return previousValue;
        },
        {},
      );

      const favoriteMajorSurveys = { ...get().favoriteMajorSurveys };

      favoriteMajorSurveys[data.majorId] = preparedData;

      set({ favoriteMajorSurveys });

      await useUserPreferencesStore.getState().setUserPreferences(userPreferences);
    },
  }),
  Object.is,
);
